import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Hash, ExternalLink } from "lucide-react";
import { ShimmerPanel } from "@/components/ui/Shimmer";

const SocialPanel = ({ socialData }) => {
  const { t } = useTranslation();
  const [displayData, setDisplayData] = useState(null);
  const hasLoadedOnce = useRef(false);
  
  // Update display data only when we have new valid data
  useEffect(() => {
    if (socialData?.social_mood) {
      setDisplayData(socialData);
      hasLoadedOnce.current = true;
    }
  }, [socialData]);
  
  // Show shimmer only on first load, never after
  if (!displayData?.social_mood) {
    if (!hasLoadedOnce.current) {
      return <ShimmerPanel type="social" />;
    }
    // After first load, keep showing old data (displayData handles this)
  }
  
  // Use displayData if available, otherwise show loading
  if (!displayData?.social_mood) {
    return <div className="empty-state">{t('analyzing')}</div>;
  }

  const { social_mood: sentiment, combined } = displayData;
  const breakdown = sentiment?.breakdown || {};
  const trendingEmotions = sentiment?.trending_emotions || [];

  // Get sentiment color
  const getSentimentColor = (sent) => {
    if (sent === 'positive') return 'var(--apple-green)';
    if (sent === 'negative') return 'var(--apple-red)';
    return 'var(--apple-text-tertiary)';
  };

  return (
    <div className="p-4" data-testid="social-panel">
      <div className="flex items-center justify-center gap-2 mb-3">
        <Hash size={16} style={{ color: '#1DA1F2' }} />
        <span className="apple-caption">{t('social')}</span>
      </div>
      <div className="text-center mb-4">
        <span className="text-xl font-bold" style={{ color: '#1DA1F2' }}>
          {sentiment?.overall?.toUpperCase().replace(/_/g, ' ') || 'ANALYZING'}
        </span>
      </div>
      
      <div className="sentiment-bar mb-1">
        <div className="sentiment-segment" style={{ width: `${(breakdown.positive || 0) * 100}%`, backgroundColor: 'var(--apple-green)' }} />
        <div className="sentiment-segment" style={{ width: `${(breakdown.neutral || 0) * 100}%`, backgroundColor: 'var(--apple-bg-tertiary)' }} />
        <div className="sentiment-segment" style={{ width: `${(breakdown.negative || 0) * 100}%`, backgroundColor: 'var(--apple-red)' }} />
      </div>
      <div className="sentiment-labels">
        <span>{Math.round((breakdown.positive || 0) * 100)}% {t('positive')}</span>
        <span>{Math.round((breakdown.negative || 0) * 100)}% {t('negative')}</span>
      </div>

      {/* Trending topics - always show section, persists until new data arrives */}
      <div className="mt-4 space-y-2">
        <div className="text-xs text-center mb-2" style={{ color: 'var(--apple-text-tertiary)' }}>
          Trending Topics
        </div>
        {trendingEmotions.length > 0 ? (
          trendingEmotions.slice(0, 4).map((item, idx) => (
            <a
              key={`${item.topic}-${idx}`}
              href={item.link || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="social-topic-link"
              data-testid={`social-topic-${idx}`}
              title={item.full_title || item.topic}
            >
              <span 
                className="social-topic-indicator"
                style={{ backgroundColor: getSentimentColor(item.sentiment) }}
              />
              <span className="social-topic-text">{item.topic}</span>
              <ExternalLink size={10} className="social-topic-icon" />
            </a>
          ))
        ) : (
          <div className="text-xs text-center py-2" style={{ color: 'var(--apple-text-tertiary)' }}>
            Loading topics...
          </div>
        )}
      </div>

      {combined && (
        <div className="alignment-badge mt-4 w-full justify-center">
          <span className="text-xs" style={{ color: 'var(--apple-text-tertiary)' }}>Alignment</span>
          <span className={`alignment-label ${
            combined.alignment > 0.7 ? 'text-green-400' : 
            combined.alignment > 0.4 ? 'text-blue-400' : 'text-orange-400'
          }`}>
            {combined.alignment_label}
          </span>
        </div>
      )}
    </div>
  );
};

export { SocialPanel };
